
export function isValidColor(value: unknown): value is string {
  if (typeof value !== 'string' || !value.trim()) return false;
  try {
    return CSS.supports('color', value.trim());
  } catch {
    return false;
  }
}

export function normalizeHex(value: string): string | null {
  const match = /^#?([0-9a-f]{3}|[0-9a-f]{6})$/i.exec(value.trim());
  if (!match) return null;
  const hex = match[1].length === 3 ? [...match[1]].map((c) => c + c).join('') : match[1];
  return `#${hex.toLowerCase()}`;
}

export function toRgb(value: string): [number, number, number] | null {
  const hex = normalizeHex(value);
  if (hex) {
    return [1, 3, 5].map((i) => parseInt(hex.slice(i, i + 2), 16)) as [number, number, number];
  }
  if (!isValidColor(value)) return null;
  try {
    const ctx = document.createElement('canvas').getContext('2d');
    if (!ctx) return null;
    ctx.fillStyle = value;
    const resolved = ctx.fillStyle;
    const fromHex = normalizeHex(resolved);
    if (fromHex) return toRgb(fromHex);
    const rgb = /rgba?\(([\d.]+),\s*([\d.]+),\s*([\d.]+)/.exec(resolved);
    return rgb ? [Number(rgb[1]), Number(rgb[2]), Number(rgb[3])] : null;
  } catch {
    return null;
  }
}

export function relativeLuminance(value: string): number | null {
  const rgb = toRgb(value);
  if (!rgb) return null;
  const [r, g, b] = rgb.map((channel) => {
    const c = channel / 255;
    return c <= 0.03928 ? c / 12.92 : ((c + 0.055) / 1.055) ** 2.4;
  });
  return 0.2126 * r + 0.7152 * g + 0.0722 * b;
}

export function contrastRatio(a: string, b: string): number | null {
  const la = relativeLuminance(a);
  const lb = relativeLuminance(b);
  if (la === null || lb === null) return null;
  const [light, dark] = la > lb ? [la, lb] : [lb, la];
  return (light + 0.05) / (dark + 0.05);
}

export function readableForeground(background: string, darkText = '#1f2937', lightText = '#ffffff'): string {
  const onDark = contrastRatio(background, lightText) ?? 0;
  const onLight = contrastRatio(background, darkText) ?? 21;
  return onLight >= onDark ? darkText : lightText;
}
